/** QuickPlan — Bibliothèque intégrée : baies et équipements 19".
 *
 * Baie : origine au coin bas-avant-gauche, largeur W selon X, profondeur P selon Y,
 * socle de 75 mm puis U unités de 44,45 mm. Rails avant : face avant à Y = F.
 * Ports « u1 » … « uN » : coin bas-gauche de chaque unité, sur la face avant du
 * rail gauche — là où vient s'appuyer l'oreille gauche d'un équipement.
 *
 * Équipement : origine = coin bas-gauche de l'oreille, face arrière de l'oreille
 * (port « u »). Les oreilles (482,6 mm hors tout) sont à Y ∈ [-2, 0], le corps de
 * 440 mm part vers +Y sur la profondeur P. Pose : attacher le port « u » d'un
 * équipement au port « uK » de la baie (K = unité basse occupée).
 */

export const U_MM = 44.45;
const EAR = 482.6;
const BODY = 440;
const FRAME = '#4a5059';
const RAIL = '#8a9099';

/** Équipement 19" générique : oreilles + corps (+ détails de face avant). */
function equipment({ id, name, nU, depth, color, front = [], price, description, paramN = false, family = 'Équipements' }) {
  const params = [{ key: 'P', label: 'Profondeur', default: depth, min: 5, max: 1000, unit: 'mm' }];
  if (paramN) params.unshift({ key: 'N', label: 'Hauteur', default: nU, options: [1, 2, 3, 4, 5, 6], unit: 'U' });
  const h = paramN ? `N*${U_MM}-0.8` : `${nU * U_MM - 0.8}`;
  return {
    id,
    name,
    category: 'rack',
    family,
    description: `${description ?? name}. Oreilles 19" (482,6 mm) à Y ∈ [-2, 0], corps de 440 mm vers +Y. Port « u » à poser sur un port « uK » de baie.`,
    color,
    params,
    parts: [
      { type: 'box', name: 'oreille g', size: [(EAR - BODY) / 2, 2, h], pos: [0, -2, 0], color: '#3a3f46' },
      { type: 'box', name: 'oreille d', size: [(EAR - BODY) / 2, 2, h], pos: [(EAR + BODY) / 2, -2, 0], color: '#3a3f46' },
      { type: 'box', name: 'corps', size: [BODY, 'P', h], pos: [(EAR - BODY) / 2, 0, 0] },
      ...front,
    ],
    ports: [{ id: 'u', label: 'Unité basse (oreille gauche)', pos: [0, 0, 0], dir: [0, -1, 0] }],
    bom: { kind: 'piece', label: paramN ? '{N}U, P {P}' : `${nU}U, P {P}`, ...(price != null ? { price } : {}) },
    // 2 vis par oreille (1 seule par oreille en 1U), têtes en façade.
    fixings: [{
      type: 'vis', d: 6, l: 16, ref: 'VIS-M6x16', name: 'Vis M6×16 + rondelle', price: 0.12,
      pos: [`(i%2)*${EAR - (EAR - BODY) / 4 * 2}+${(EAR - BODY) / 4}`, -2, `6.35+floor(i/2)*((${h})-12.7)`],
      dir: [0, -1, 0], repeat: { count: paramN ? 'min(4, 2*N)' : Math.min(4, 2 * nU), var: 'i' },
    }],
    kit: [{ ref: 'CAGE-M6', name: 'Écrou cage M6', qty: paramN ? 'min(4, 2*N)' : Math.min(4, 2 * nU), price: 0.25 }],
  };
}

/** Rangée de ports RJ45 en face avant (petites alvéoles sombres). */
function portRow(count, z, x0 = 60, pitch = 14) {
  return [{
    type: 'box', name: 'ports', size: [10, 3, 9], pos: [`${x0}+i*${pitch}`, -1, z], color: '#111317',
    repeat: { count, var: 'i' },
  }];
}

const out = [
  {
    id: 'rk-baie',
    name: 'Baie 19"',
    category: 'rack',
    family: 'Baies',
    description: 'Baie 19" sur socle : 4 montants, toit, socle, rails avant et arrière, capots latéraux optionnels (C = 1). Ports u1…uU sur le rail avant gauche.',
    color: FRAME,
    params: [
      { key: 'U', label: 'Hauteur utile', default: 42, min: 4, max: 52, step: 1, unit: 'U' },
      { key: 'W', label: 'Largeur', default: 800, options: [600, 800], unit: 'mm' },
      { key: 'P', label: 'Profondeur', default: 800, options: [450, 600, 800, 1000, 1200], unit: 'mm' },
      { key: 'F', label: 'Retrait rails avant', default: 70, min: 30, max: 300, unit: 'mm' },
      { key: 'C', label: 'Capots latéraux (0/1)', default: 1, options: [0, 1] },
    ],
    parts: [
      { type: 'box', name: 'socle', size: ['W', 'P', 75] },
      { type: 'box', name: 'montant', size: [40, 40, `U*${U_MM}+60`], pos: ['(i%2)*(W-40)', 'floor(i/2)*(P-40)', 75], repeat: { count: 4, var: 'i' } },
      { type: 'box', name: 'toit', size: ['W', 'P', 20], pos: [0, 0, `75+U*${U_MM}+40`] },
      { type: 'box', name: 'rail av', size: [17, 20, `U*${U_MM}`], pos: [`(W-${EAR})/2+(i*(${EAR}-17))`, 'F', 75], color: RAIL, repeat: { count: 2, var: 'i' } },
      { type: 'box', name: 'rail ar', size: [17, 20, `U*${U_MM}`], pos: [`(W-${EAR})/2+(i*(${EAR}-17))`, 'P-F-20', 75], color: RAIL, repeat: { count: 2, var: 'i' } },
      { type: 'box', name: 'traverse', size: ['W-80', 'P-80', 20], pos: [40, 40, `75+U*${U_MM}+20`] },
      { type: 'box', name: 'capot', size: [2, 'P-80', `U*${U_MM}+60`], pos: ['i*(W-2)', 40, 75], color: '#3d434b', repeat: { count: 'C*2', var: 'i' } },
    ],
    ports: [
      { id: 'u{i+1}', label: 'Unité {i+1}', pos: [`(W-${EAR})/2`, 'F', `75+i*${U_MM}`], dir: [0, -1, 0], repeat: { count: 'U', var: 'i' } },
      { id: 'sol', pos: [0, 0, 0] },
    ],
    bom: { kind: 'piece', label: '{U}U {W}×{P}', price: '350+U*12+(W-600)/2+(P-600)/2+C*120' },
  },
  {
    id: 'rk-porte',
    name: 'Porte vitrée de baie',
    category: 'rack',
    family: 'Baies',
    description: 'Porte avant vitrée pour baie W × (U unités). À ATTACHER à la baie (parent) à la position [0, -20, 75] : cadre W × hauteur utile + 60, verre fumé. ARTICULÉE : charnière à droite (C = 1) ou à gauche (C = 0), ouverture 0 → 120°.',
    color: '#4a5059',
    params: [
      { key: 'U', label: 'Hauteur utile', default: 42, min: 4, max: 52, unit: 'U' },
      { key: 'W', label: 'Largeur', default: 800, options: [600, 800], unit: 'mm' },
      { key: 'C', label: 'Charnières (0 gauche, 1 droite)', default: 1, options: [0, 1] },
    ],
    parts: [
      { type: 'box', name: 'montant', size: [30, 20, `U*${U_MM}+60`], pos: ['i*(W-30)', 0, 0], repeat: { count: 2, var: 'i' } },
      { type: 'box', name: 'traverse', size: ['W-60', 20, 30], pos: [30, 0, `i*(U*${U_MM}+30)`], repeat: { count: 2, var: 'i' } },
      { type: 'box', name: 'vitrage', size: ['W-60', 4, `U*${U_MM}`], pos: [30, 8, 30], color: '#7fa7c9', opacity: 0.28 },
      { type: 'box', name: 'poignée', size: [14, 30, 160], pos: [`(1-C)*(W-50)+C*36`, -30, `U*${U_MM}/2-50`], color: '#22262b' },
    ],
    ports: [{ id: 'bas-g', pos: [0, 0, 0] }],
    joint: { type: 'hinge', label: 'Ouverture (°)', axis: [0, 0, '2*C-1'], origin: ['C*W', 0, 0], min: 0, max: 120 },
    bom: { kind: 'piece', label: '{U}U, larg. {W}', price: '120+U*3' },
  },
  equipment({
    id: 'rk-serveur', name: 'Serveur', nU: 2, depth: 700, color: '#30353c', paramN: true,
    description: 'Serveur rackable',
    front: [{ type: 'box', name: 'façade', size: [380, 3, `N*${U_MM}-8`], pos: [51, -1, 3.6], color: '#1d2126' }],
  }),
  equipment({
    id: 'rk-switch24', name: 'Switch 24 ports', nU: 1, depth: 250, color: '#3d4f63', price: undefined,
    description: 'Commutateur 24 ports RJ45 + 4 SFP',
    front: portRow(24, 17),
  }),
  equipment({
    id: 'rk-switch48', name: 'Switch 48 ports', nU: 1, depth: 300, color: '#3d4f63',
    description: 'Commutateur 48 ports RJ45 + 4 SFP',
    front: [...portRow(24, 26), ...portRow(24, 9)],
  }),
  equipment({
    id: 'rk-brassage24', name: 'Panneau de brassage 24 ports', nU: 1, depth: 110, color: '#2a2e33', price: 45,
    description: 'Panneau de brassage RJ45 Cat6, 24 ports',
    front: portRow(24, 17, 75, 14),
  }),
  equipment({
    id: 'rk-passe-cables', name: 'Passe-câbles 1U', nU: 1, depth: 80, color: '#2a2e33', price: 15,
    description: 'Panneau passe-câbles à anneaux',
    front: [{ type: 'box', name: 'anneau', size: [30, 50, 30], pos: ['40+i*80', -50, 7], color: '#1d2126', repeat: { count: 5, var: 'i' } }],
  }),
  equipment({
    id: 'rk-tablette', name: 'Tablette fixe', nU: 1, depth: 550, color: '#2f3338', price: 40, paramN: true,
    description: 'Tablette fixe ventilée',
  }),
  equipment({
    id: 'rk-pdu', name: 'Bandeau de prises (PDU) 8 prises', nU: 1, depth: 60, color: '#22262b', price: 35,
    description: 'Bandeau 8 prises 2P+T avec interrupteur',
    front: [{ type: 'box', name: 'prise', size: [36, 3, 30], pos: ['40+i*48', -1, 6.5], color: '#111317', repeat: { count: 8, var: 'i' } }],
  }),
  equipment({
    id: 'rk-onduleur', name: 'Onduleur', nU: 2, depth: 600, color: '#1f2226', paramN: true,
    description: 'Onduleur rackable',
    front: [{ type: 'box', name: 'écran', size: [60, 3, 25], pos: [210, -1, 30], color: '#4c7aa6' }],
  }),
  equipment({
    id: 'rk-obturateur', name: 'Obturateur', nU: 1, depth: 3, color: '#2a2e33', price: 8, paramN: true,
    description: 'Panneau obturateur plein',
  }),
];

export default out;
